import { SUPPORTED_CEX } from "./supported-cex";

/**
 * Taker fee (percent, e.g. `0.1` = 0.1%) charged by an exchange on a spot market order.
 * `notConfigured` carries WHY the fee is absent — a campaign on that exchange halts the
 * dynamic volume-target computation and keeps its manual target instead of assuming a rate.
 * A real 0% venue is `{ takerFeePercent: 0 }`, never `notConfigured`.
 */
export type ExchangeTakerFee = { readonly takerFeePercent: number } | { readonly notConfigured: string };

/**
 * Total over SUPPORTED_CEX so adding a CEX fails the build until its taker fee is filled in.
 * Venues outside this map (perps DEX, onchain pools) resolve to `undefined` — they don't run
 * spot trade-to-earn campaigns.
 */
export const EXCHANGE_TAKER_FEE: Record<SUPPORTED_CEX, ExchangeTakerFee> = {
  [SUPPORTED_CEX.MEXC]: { takerFeePercent: 0.02 },
  [SUPPORTED_CEX.GATEIO]: { takerFeePercent: 0.09 },
  [SUPPORTED_CEX.BYBIT]: { takerFeePercent: 0.1 },
  [SUPPORTED_CEX.BITGET]: { takerFeePercent: 0.1 },
  [SUPPORTED_CEX.BINANCE]: { takerFeePercent: 0.1 },
  [SUPPORTED_CEX.HTX]: { takerFeePercent: 0.2 },
  [SUPPORTED_CEX.OKX]: { takerFeePercent: 0.23 },
  // Tiered by pair class (A 0.1% / B 0.2% / C 0.3%) with no per-pair source here, so the
  // worst class is used: a fee guessed too low over-sizes the epoch and users trade at a loss.
  [SUPPORTED_CEX.KUCOIN]: { takerFeePercent: 0.3 },
  [SUPPORTED_CEX.KRAKEN]: { takerFeePercent: 0.4 },
  [SUPPORTED_CEX.CRYPTOCOM]: { takerFeePercent: 0.5 },
  [SUPPORTED_CEX.COINBASE]: { takerFeePercent: 0.6 },
  [SUPPORTED_CEX.BITMART]: { notConfigured: "not in the taker-fee reference table" },
  [SUPPORTED_CEX.BITHUMB]: { notConfigured: "not in the taker-fee reference table" },
  [SUPPORTED_CEX.LBANK]: { notConfigured: "not in the taker-fee reference table" },
  // Perps venue — the spot taker fee the volume target is built on doesn't apply.
  [SUPPORTED_CEX.KRAKEN_FUTURES]: { notConfigured: "perps venue, not a spot trade-to-earn market" },
};

const CONFIGURED_EXCHANGE_IDS = new Set<string>(Object.keys(EXCHANGE_TAKER_FEE));

/**
 * Venues outside `SUPPORTED_CEX` that still run trade-to-earn campaigns. They charge a
 * different taker fee per market, so the entry is keyed by it rather than collapsed to one
 * number: Hyperliquid's spot taker is materially above its perps taker, and pricing a spot
 * epoch on the perps rate under-states the cost and over-sizes the target.
 *
 * Base-tier rates. A campaign whose accounts sit in a discounted volume tier sets
 * `takerFeePercentOverride` on its epoch group instead.
 */
export const MARKET_TAKER_FEE: Record<string, Record<"spot" | "perps", ExchangeTakerFee>> = {
  hyperliquid: {
    spot: { takerFeePercent: 0.07 },
    perps: { takerFeePercent: 0.045 },
  },
};

/**
 * `undefined` when we hold no fee entry for the venue. `marketType` only matters for venues
 * trading several markets; an unknown one takes the dearest entry, since a fee guessed too low
 * over-sizes the epoch and users trade at a loss (same reasoning as KuCoin's worst class).
 */
export function findExchangeTakerFee(
  exchangeId: string,
  marketType?: "spot" | "perps" | null,
): ExchangeTakerFee | undefined {
  if (CONFIGURED_EXCHANGE_IDS.has(exchangeId)) return EXCHANGE_TAKER_FEE[exchangeId as SUPPORTED_CEX];

  const byMarket = MARKET_TAKER_FEE[exchangeId];
  if (!byMarket) return undefined;
  if (marketType) return byMarket[marketType];

  return [byMarket.spot, byMarket.perps].reduce((dearest, candidate) =>
    "takerFeePercent" in candidate &&
    "takerFeePercent" in dearest &&
    candidate.takerFeePercent > dearest.takerFeePercent
      ? candidate
      : dearest,
  );
}
